import React, { useState, useEffect } from "react";
import Select from "react-select"; // New import for searchable dropdowns
import "../../css/ServiceRequestForm.css";
import { useTranslation } from "react-i18next";
import { isMobile } from "react-device-detect";
import { faHeadset } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const loadingGif = process.env.REACT_APP_PUBLIC_URL + '/loading1.gif';

const ServiceRequestForm = ({ data, language, isDisabled, onSendMessage }) => {
  const { t } = useTranslation();

  // Autofill fields
  const [entity, setEntity] = useState("");
  const [employeeID, setEmployeeID] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [subject, setSubject] = useState(data?.issue_subject ?? "");
  const [csrfToken, setCsrfToken] = useState();
  const [cookies, setCookies] = useState("");

  // Validation and API error states
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");

  // Dropdown states
  const [serviceCategories, setServiceCategories] = useState([]);
  const [selectedService, setSelectedService] = useState("");
  const [subCategory1, setSubCategory1] = useState([]);
  const [selectedSubCategory1, setSelectedSubCategory1] = useState("");
  const [subCategory2, setSubCategory2] = useState([]);
  const [selectedSubCategory2, setSelectedSubCategory2] = useState("");
  const [subCategory3, setSubCategory3] = useState([]);
  const [selectedSubCategory3, setSelectedSubCategory3] = useState("");
  const [issueDescription, setIssueDescription] = useState(data?.issue_description ?? "");
  const [isSpecialNeeds, setIsSpecialNeeds] = useState(false);

  // Loading state for both employee data and sending email
  const [isLoading, setIsLoading] = useState(false);

  function convertToCookieString(jsonObject) {
    const cookieArray = [];
    for (const [key, value] of Object.entries(jsonObject)) {
      cookieArray.push(`${key}=${value}`);
    }
    return cookieArray.join("; ");
  }

  // Load employee data on component mount using getEmployeeData
  useEffect(() => {
    setIsLoading(true);
    const fetchEmployeeData = async () => {
      const frontendLog = JSON.stringify({
        sessionID: sessionStorage.getItem("sessionID"),
        EmployeeID: localStorage.getItem("EmployeeID"),
        channel: isMobile ? "Mobile" : "Portal",
        language: localStorage.getItem("language"),
      });

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/middleware/getEmployeeData?code=${process.env.REACT_APP_MIDDLEWARE_FUNCTION_KEY}&token=${encodeURIComponent(
            localStorage.getItem("id_token")
          )}&sessionID=${encodeURIComponent(
            sessionStorage.getItem("sessionID")
          )}&frontendLog=${encodeURIComponent(frontendLog)}`
        );
        const emp = await response.json();
        // Update state with API response using language prop
        setEmployeeID(emp.EmployeeId);
        setEmployeeName(language === "ar" ? emp.NameAr : emp.NameEn);
        setEmail(emp.Email);
        setMobile(emp.Phone);
        setEntity(language === "ar" ? emp.EntityNameAr : emp.EntityNameEn);
        setCsrfToken(emp.csrfToken);
        setCookies(convertToCookieString(emp.cookies));
      } catch (error) {
        console.error("Error fetching employee data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEmployeeData();
  }, [language]);

  // Check special needs status on mount
  useEffect(() => {
    const checkSpecialNeedsStatus = async () => {
      const frontendLog = JSON.stringify({
        sessionID: sessionStorage.getItem("sessionID"),
        EmployeeID: localStorage.getItem("EmployeeID"),
        channel: isMobile ? "Mobile" : "Portal",
        language: localStorage.getItem("language"),
      });

      try {
        const resp = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/middleware/getUserSpecialNeedsStatus?code=${process.env.REACT_APP_MIDDLEWARE_FUNCTION_KEY}&token=${encodeURIComponent(
            localStorage.getItem("id_token")
          )}&sessionID=${encodeURIComponent(
            sessionStorage.getItem("sessionID")
          )}&frontendLog=${encodeURIComponent(frontendLog)}`
        );

        // Initial value from data.is_special_needs
        const isSpecialFromData = Number(data?.is_special_needs) === 1;

        if (isSpecialFromData) {
          // Always checked if 1
          setIsSpecialNeeds(true);
        } else {
          // If 0 → check API
          if (resp.status === 200) {
            setIsSpecialNeeds(true);
          } else {
            setIsSpecialNeeds(false);
          }
        }

        // Special case: data is 1 and API is 400 → still checked (already handled above)
      } catch (err) {
        console.error("Error checking special needs status:", err);
        setIsSpecialNeeds(false);
      }
    };

    checkSpecialNeedsStatus();
  }, []);

  // Fetch function for dropdowns remains unchanged
  const fetchData = (setter, params) => {
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/chatbot/categories?code=${process.env.REACT_APP_CHATBOT_FUNCTION_KEY}&language=${encodeURIComponent(
        language
      )}${params}`
    )
      .then((response) => response.json())
      .then((data) => setter(data))
      .catch((error) => console.error("Error fetching data:", error));
  };

  // Fetch service categories
  useEffect(() => {
    fetchData(setServiceCategories, "");
  }, [language]);

  // Fetch sub-category 1 when a service is selected
  useEffect(() => {
    if (selectedService) {
      fetchData(setSubCategory1, `&service=${encodeURIComponent(selectedService)}`);
    } else {
      setSubCategory1([]);
      setSelectedSubCategory1("");
    }
  }, [selectedService]);

  // Fetch sub-category 2 when sub-category 1 is selected
  useEffect(() => {
    if (selectedSubCategory1) {
      fetchData(
        setSubCategory2,
        `&service=${encodeURIComponent(selectedService)}&sub_service_1=${encodeURIComponent(
          selectedSubCategory1
        )}`
      );
    } else {
      setSubCategory2([]);
      setSelectedSubCategory2("");
    }
  }, [selectedSubCategory1]);

  // Fetch sub-category 3 when sub-category 2 is selected
  useEffect(() => {
    if (selectedSubCategory2) {
      fetchData(
        setSubCategory3,
        `&service=${encodeURIComponent(selectedService)}&sub_service_1=${encodeURIComponent(
          selectedSubCategory1
        )}&sub_service_2=${encodeURIComponent(selectedSubCategory2)}`
      );
    } else {
      setSubCategory3([]);
      setSelectedSubCategory3("");
    }
  }, [selectedSubCategory2]);

  // Submit form with validation and call sendEmail endpoint
  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    // Validate Email: check required and format
    if (!email.trim()) {
      newErrors.email = t("Email is required");
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      newErrors.email = t("Please enter a valid email address");
    }

    if (!mobile.trim()) {
      newErrors.mobile = t("Mobile is required");
    } else if (!/^\d{8}$/.test(mobile)) {
      newErrors.mobile = t("Mobile must be exactly 8 digits");
    }
    if (!subject.trim()) newErrors.subject = t("Subject is required");
    if (!selectedService) newErrors.selectedService = t("Service Category is required");
    if (!selectedSubCategory1)
      newErrors.selectedSubCategory1 = t("Sub-Category 1 is required");
    if (!selectedSubCategory2)
      newErrors.selectedSubCategory2 = t("Sub-Category 2 is required");
    if (subCategory3 && subCategory3.length > 0 && !selectedSubCategory3) {
      newErrors.selectedSubCategory3 = t("Sub-Category 3 is required");
    }
    if (!issueDescription.trim())
      newErrors.issueDescription = t("Issue Description is required");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setApiError("");

    // Build frontendLog used for logging and tracing
    const frontendLog = JSON.stringify({
      sessionID: sessionStorage.getItem("sessionID"),
      EmployeeID: employeeID,
      channel: isMobile ? "Mobile" : "Portal",
      language: language,
    });

    // Build the sendEmail API URL using the environment variable and required query parameters.
    const url =
      `${process.env.REACT_APP_BACKEND_URL}/middleware/sendEmail?code=${process.env.REACT_APP_MIDDLEWARE_FUNCTION_KEY}` +
      `&assertion=${encodeURIComponent(localStorage.getItem("id_token"))}` +
      `&sessionID=${encodeURIComponent(sessionStorage.getItem("sessionID"))}` +
      `&frontendLog=${encodeURIComponent(frontendLog)}` +
      `&x-csrf-token=${csrfToken}` +
      `&cookies=${cookies}`;

    // Build the request body matching the backend structure.
    const requestBody = {
      EmployeeId: employeeID,
      Language: language.toUpperCase(),
      Name: employeeName,
      Email: email,
      Phone: mobile,
      EntityName: entity,
      Categroy1: selectedSubCategory1,
      Categroy2: selectedSubCategory2,
      Categroy3: selectedSubCategory3,
      IssueDescription: issueDescription,
      TicketIssue: subject,
      SpecialNeeds: isSpecialNeeds ? "X" : ""
    };

    setIsLoading(true);
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept-Language": language.toUpperCase()
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        setApiError("");
        onSendMessage({ sender: "bot", text: t("Your request has been received successfully, You will be responded to shortly") });
        setTimeout(() => {
          onSendMessage({
            sender: 'bot',
            isRating: true,
            requestType: "success",
            timestamp: new Date().getTime(),
          });
        }, (process.env.REACT_APP_TIME_TO_DISPLAY_REVIEW * 1000));
      } else {
        const errorData = await response.json();
        setApiError(t("There was an error occurred, Please try again later."));
      }
    } catch (error) {
      console.error("Error sending email:", error);
      setApiError(t("There was an error occurred, Please try again later."));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`service-request-box ${isDisabled ? "form-disabled" : ""}`}>
      {/* Loader overlay */}
      {isLoading && (
        <div className="centered-v-h loading-overlay">
          <img className="img-bottom" src={loadingGif} alt="Loading..." width={70} height={70} />
          <span className="span-bottom">{t('Processing, please wait ...')}</span>
        </div>
      )}
      <div className="form-card">
        <div className="form-card-title">
          {t("Tech Support Request")}
        </div>
        <form onSubmit={handleSubmit} className="form-container">
          {/* Autofill Fields */}
          <div className="form-row">
            <div className="form-group">
              <label>{t("Language")}</label>
              <input
                type="text"
                value={language === "ar" ? "العربية" : language === "en" ? "English" : language}
                disabled
                className="input-field"
              />
            </div>
            <div className="form-group">
              <label>{t("Entity")}</label>
              <input type="text" value={entity} disabled className="input-field" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>{t("Employee ID")}</label>
              <input type="text" value={employeeID} disabled className="input-field" />
            </div>
            <div className="form-group">
              <label>{t("Employee Name")}</label>
              <input type="text" value={employeeName} disabled className="input-field" />
            </div>
          </div>

          {/* Editable Fields */}
          <div className="form-row">
            {/* Email Input on its own row */}
            <div className="form-group">
              <label>{t("Email")}</label>
              <input
                type="text"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (e.target.value.trim() !== "") {
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.email;
                      return newErrors;
                    });
                  }
                }}
                className="input-field"
                disabled={isDisabled}
              />
              {errors.email && (
                <label className="error-label">{errors.email}</label>
              )}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>{t("Mobile")}</label>
              <input
                type="text"
                value={mobile}
                onChange={(e) => {
                  setMobile(e.target.value);
                  if (e.target.value.trim() !== "") {
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.mobile;
                      return newErrors;
                    });
                  }
                }}
                className="input-field"
                disabled={isDisabled}
              />
              {errors.mobile && (
                <label className="error-label">{errors.mobile}</label>
              )}
            </div>
          </div>
          <div className="mb-3">
            <label>
              <input
                type="checkbox"
                checked={isSpecialNeeds}
                onChange={(e) => setIsSpecialNeeds(e.target.checked)}
                disabled={isDisabled}
              />
              {t("Special Needs")}
            </label>
          </div>
          {/* Dropdowns using react-select */}
          <div className="form-row">
            <div className="form-group">
              <label>{t("Service Category")}</label>
              <Select
                value={selectedService ? { value: selectedService, label: selectedService } : null}
                onChange={(option) => {
                  setSelectedService(option ? option.value : "");
                  if (option && option.value !== "") {
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.selectedService;
                      return newErrors;
                    });
                  }
                }}
                styles={{
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                  menuList: (base) => ({
                    ...base,
                    maxHeight: '200px',
                    overflowY: 'auto',
                  }),
                }}
                options={serviceCategories.map((service) => ({
                  value: service.serviceName,
                  label: service.serviceName,
                }))}
                isDisabled={isDisabled}
                placeholder={t("Select a service")}
                noOptionsMessage={() => t("No options found")}
                className="input-field"
              />
              {errors.selectedService && (
                <label className="error-label">{errors.selectedService}</label>
              )}
            </div>
            <div className="form-group">
              <label>{t("Sub-Category 1")}</label>
              <Select
                value={selectedSubCategory1 ? { value: selectedSubCategory1, label: selectedSubCategory1 } : null}
                onChange={(option) => {
                  setSelectedSubCategory1(option ? option.value : "");
                  if (option && option.value !== "") {
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.selectedSubCategory1;
                      return newErrors;
                    });
                  }
                }}
                styles={{
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                  menuList: (base) => ({
                    ...base,
                    maxHeight: '200px',
                    overflowY: 'auto',
                  }),
                }}
                options={subCategory1.map((sub) => ({
                  value: sub.name,
                  label: sub.name,
                }))}
                isDisabled={isDisabled}
                placeholder={t("Select a sub-category")}
                noOptionsMessage={() => t("No options found")}
                className="input-field"
              />
              {errors.selectedSubCategory1 && (
                <label className="error-label">{errors.selectedSubCategory1}</label>
              )}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>{t("Sub-Category 2")}</label>
              <Select
                value={selectedSubCategory2 ? { value: selectedSubCategory2, label: selectedSubCategory2 } : null}
                onChange={(option) => {
                  setSelectedSubCategory2(option ? option.value : "");
                  if (option && option.value !== "") {
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.selectedSubCategory2;
                      return newErrors;
                    });
                  }
                }}
                styles={{
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                  menuList: (base) => ({
                    ...base,
                    maxHeight: '200px',
                    overflowY: 'auto',
                  }),
                }}
                options={subCategory2.map((sub) => ({
                  value: sub.name,
                  label: sub.name,
                }))}
                isDisabled={isDisabled}
                placeholder={t("Select a sub-category")}
                noOptionsMessage={() => t("No options found")}
                className="input-field"
              />
              {errors.selectedSubCategory2 && (
                <label className="error-label">{errors.selectedSubCategory2}</label>
              )}
            </div>
            <div className="form-group">
              <label>{t("Sub-Category 3")}</label>
              <Select
                value={selectedSubCategory3 ? { value: selectedSubCategory3, label: selectedSubCategory3 } : null}
                onChange={(option) => {
                  setSelectedSubCategory3(option ? option.value : "");
                  if (option && option.value !== "") {
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.selectedSubCategory3;
                      return newErrors;
                    });
                  }
                }}
                styles={{
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                  menuList: (base) => ({
                    ...base,
                    maxHeight: '200px',
                    overflowY: 'auto',
                  }),
                }}
                options={subCategory3.map((sub) => ({
                  value: sub.name,
                  label: sub.name,
                }))}
                isDisabled={isDisabled}
                placeholder={t("Select a sub-category")}
                noOptionsMessage={() => t("No options found")}
                className="input-field"
              />
              {errors.selectedSubCategory3 && (
                <label className="error-label">{errors.selectedSubCategory3}</label>
              )}
            </div>
          </div>

          {/* Subject Input */}
          <div className="form-group full-width">
            <label>{t("Subject")}</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
                if (e.target.value.trim() !== "") {
                  setErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors.subject;
                    return newErrors;
                  });
                }
              }}
              className="input-field"
              disabled={isDisabled}
            />
            {errors.subject && (
              <label className="error-label">{errors.subject}</label>
            )}
          </div>

          {/* Issue Description */}
          <div className="form-group full-width">
            <label>{t("Issue Description")}</label>
            <textarea
              className="input-field"
              value={issueDescription}
              onChange={(e) => {
                setIssueDescription(e.target.value);
                if (e.target.value.trim() !== "") {
                  setErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors.issueDescription;
                    return newErrors;
                  });
                }
              }}
              disabled={isDisabled}
            />
            {errors.issueDescription && (
              <label className="error-label">{errors.issueDescription}</label>
            )}
          </div>
          <button type="submit" className="submit-button" disabled={isDisabled}>
            {t("Submit")}
          </button>
        </form>
      </div>
      {apiError && <div className="error-message">{apiError}</div>}
    </div>
  );
};

export default ServiceRequestForm;